<template>
  <div ref="chart" class="echarts-stacked-bar-chart" role="img" :aria-label="ariaLabel" />
</template>

<script>
import echarts, { chartAnimationDuration } from "./echarts-core"

const integerFormatter = new Intl.NumberFormat("en-CA", { maximumFractionDigits: 0 })

export default {
  name: "EChartsStackedBarChart",
  props: {
    labels: { type: Array, required: true },
    series: { type: Array, required: true },
    isDark: { type: Boolean, default: false },
    ariaLabel: { type: String, default: "Stacked bar chart" },
  },
  data() {
    return { chart: null, resizeObserver: null }
  },
  mounted() { this.$nextTick(this.initChart) },
  beforeDestroy() {
    window.removeEventListener("resize", this.refresh)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.chart) this.chart.dispose()
  },
  watch: {
    labels: { handler() { this.updateChart() }, deep: true },
    series: { handler() { this.updateChart() }, deep: true },
    isDark() { this.updateChart() },
  },
  methods: {
    initChart() {
      if (!this.$refs.chart || this.chart) return
      this.chart = echarts.init(this.$refs.chart, null, { renderer: "canvas" })
      window.addEventListener("resize", this.refresh)
      if (typeof ResizeObserver !== "undefined") {
        this.resizeObserver = new ResizeObserver(this.refresh)
        this.resizeObserver.observe(this.$refs.chart)
      }
      this.updateChart()
    },
    updateChart() {
      if (!this.chart) return this.$nextTick(this.initChart)
      const textColor = this.isDark ? "rgba(255,255,255,0.72)" : "rgba(0,0,0,0.6)"
      const gridColor = this.isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"
      this.chart.setOption({
        animationDuration: chartAnimationDuration(),
        color: this.series.map(item => item.color),
        grid: { top: 42, right: 18, bottom: 28, left: 16, containLabel: true },
        legend: { top: 0, textStyle: { color: textColor, fontSize: 11 } },
        tooltip: {
          trigger: "axis",
          renderMode: "richText",
          confine: true,
          axisPointer: { type: "shadow" },
          backgroundColor: this.isDark ? "rgba(35,35,35,0.96)" : "rgba(20,20,20,0.88)",
          borderWidth: 0,
          textStyle: { color: "#fff", fontSize: 12 },
          formatter: points => [points[0]?.axisValue, ...points.map(point => `${point.marker} ${point.seriesName}: ${integerFormatter.format(point.value)}`)].join("\n"),
        },
        xAxis: {
          type: "category",
          data: this.labels,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: gridColor } },
          axisLabel: { color: textColor, hideOverlap: true, margin: 12 },
        },
        yAxis: {
          type: "value",
          minInterval: 1,
          axisLabel: { color: textColor, formatter: value => integerFormatter.format(value) },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: gridColor } },
        },
        series: this.series.map(item => ({
          name: item.name,
          type: "bar",
          stack: "customers",
          data: item.values,
          itemStyle: { borderRadius: item.name === this.series[this.series.length - 1]?.name ? [4, 4, 0, 0] : 0 },
          emphasis: { focus: "series" },
        })),
      }, true)
      this.refresh()
    },
    refresh() { this.$nextTick(() => this.chart?.resize()) },
  },
}
</script>

<style scoped>
.echarts-stacked-bar-chart { width: 100%; height: 100%; min-height: 300px; }
</style>
